import {
  ArrowLeft,
  Camera,
  Check,
  CheckCircle2,
  Download,
  FileUp,
  RotateCcw,
  Send
} from "lucide-react";
import {
  useEffect,
  useId,
  useState,
  type ChangeEvent,
  type FC,
  type ReactNode
} from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { decodeBase64ToBlob } from "@/api/utils/file";
import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { ClientPortalAPI, portalErrorCode } from "../api";
import { useClientPortal } from "../components/ClientPortalShell";
import { PortalCard } from "../components/PortalParts";
import { PortalPdf } from "../components/PortalPdf";
import { SignaturePad, type SignatureValue } from "../components/SignaturePad";
import { UploadCheckCard } from "../components/UploadCheckCard";
import { usePortalT } from "../i18n";
import {
  refreshClientPortal,
  useClientPortalSignature,
  useClientPortalSignatureFile
} from "../queries";
import { CLIENT_PORTAL_TASKS_PATH } from "../routes";
import type { InkUpload, PortalFile, PortalSignature } from "../types";
import { PortalLoadError } from "./PortalHomePage";

type T = ReturnType<typeof usePortalT>["t"];

const senderFirst = (s: PortalSignature, t: T): string =>
  s.from?.trim().split(/\s+/)[0] || t("explainer.yourTeamLower");

const longDate = (iso: string | null, language: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(d);
};

const download = (file: PortalFile) => {
  const url = URL.createObjectURL(
    decodeBase64ToBlob(file.data, file.content_type)
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = file.filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

const Step: FC<{
  n: number;
  title: string;
  done?: boolean;
  children?: ReactNode;
}> = ({ n, title, done = false, children }) => (
  <li className="flex gap-3">
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        done ? "bg-success text-white" : "bg-primary text-primary-foreground"
      )}
      aria-hidden
    >
      {done ? <Check className="size-3.5" strokeWidth={3} /> : n}
    </span>
    <div className="flex min-w-0 flex-1 flex-col gap-2.5 pb-1">
      <span className="text-sm font-semibold leading-6">{title}</span>
      {children}
    </div>
  </li>
);

const Done: FC<{ title: string; body: string; children?: ReactNode }> = ({
  title,
  body,
  children
}) => (
  <div className="flex flex-col gap-3" role="status">
    <span className="flex items-center gap-2 text-base font-semibold text-success">
      <CheckCircle2 className="size-5" />
      {title}
    </span>
    <p className="text-sm text-muted-foreground">{body}</p>
    {children}
  </div>
);

const EsignSteps: FC<{
  signature: PortalSignature;
  file: PortalFile | undefined;
  preview: boolean;
  defaultName: string;
}> = ({ signature, file, preview, defaultName }) => {
  const { t, i18n } = usePortalT();
  const [name, setName] = useState(defaultName);
  const [value, setValue] = useState<SignatureValue | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(true);
  const lead = senderFirst(signature, t);

  if (signature.status !== "pending")
    return (
      <Done
        title={t("sign.signed")}
        body={t(emailed ? "sign.signedBody" : "sign.signedNoCopy", {
          date: longDate(signature.signed_at, i18n.language)
        })}
      >
        {file ? (
          <Button variant="outline" onClick={() => download(file)}>
            <Download />
            {t("sign.downloadSigned")}
          </Button>
        ) : null}
      </Done>
    );

  const submit = () => {
    if (!value || !consent) {
      setError(t(value ? "sign.needConsent" : "sign.needSignature"));
      return;
    }
    setError(null);
    setBusy(true);
    ClientPortalAPI.sign(signature.id, {
      method: value.method,
      name: name.trim(),
      image: value.image,
      consent
    })
      .then((signed) => {
        setEmailed(signed.copy_emailed);
        return refreshClientPortal();
      })
      .catch((e: unknown) => {
        const code = portalErrorCode(e);
        setError(
          code === "changed"
            ? t("sign.changed")
            : code === "not_pending" || code === "gone"
            ? t("sign.gone")
            : t("sign.failed")
        );
      })
      .finally(() => setBusy(false));
  };

  const disabled = preview || busy;
  return (
    <>
      <ol className="flex flex-col gap-5">
        <Step n={1} title={t("sign.read")} done>
          <span className="text-sm text-muted-foreground">
            {t("sign.readHint")}
          </span>
        </Step>
        <Step n={2} title={t("sign.add")}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="signer-name">{t("sign.fullName")}</Label>
            <Input
              id="signer-name"
              value={name}
              autoComplete="name"
              disabled={disabled}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <SignaturePad name={name} disabled={disabled} onChange={setValue} />
          <div className="flex items-start gap-2.5">
            <Checkbox
              id="esign-consent"
              checked={consent}
              disabled={disabled}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="esign-consent"
              className="text-sm font-normal leading-snug"
            >
              {t("sign.consent")}
            </Label>
          </div>
        </Step>
      </ol>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <Button
        variant="primary"
        size="lg"
        className="w-full"
        disabled={disabled || !name.trim()}
        title={preview ? t("sign.previewOnly") : undefined}
        onClick={submit}
        data-testid="esign-submit"
      >
        {busy ? t("sign.signing") : t("sign.signAndSend", { name: lead })}
      </Button>
      <p className="text-xs text-muted-foreground">{t("sign.audit")}</p>
    </>
  );
};

/** A styled label for a hidden file input; `camera` opens the back camera on phones. */
const FilePick: FC<{
  camera: boolean;
  disabled: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  testId?: string;
  children: ReactNode;
}> = ({ camera, disabled, onChange, className, testId, children }) => {
  const id = useId();
  return (
    <>
      <input
        id={id}
        type="file"
        className="sr-only"
        accept={camera ? "image/*" : "image/*,application/pdf"}
        capture={camera ? "environment" : undefined}
        disabled={disabled}
        onChange={onChange}
        data-testid={testId}
      />
      <label
        htmlFor={id}
        className={cn(
          "inline-flex cursor-pointer items-center text-sm font-medium",
          disabled && "pointer-events-none opacity-50",
          className
        )}
      >
        {children}
      </label>
    </>
  );
};

const boxLine = (s: PortalSignature, t: T): string[] =>
  s.pages.map((page) => {
    const box = s.boxes.find((b) => b.page === page)?.label;
    return box
      ? t("sign.inkBox", { box, page })
      : t("sign.inkBoxPage", { page });
  });

const InkSteps: FC<{
  signature: PortalSignature;
  file: PortalFile | undefined;
  preview: boolean;
}> = ({ signature, file, preview }) => {
  const { t } = usePortalT();
  const lead = senderFirst(signature, t);
  const [photo, setPhoto] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [ink, setInk] = useState<InkUpload | null>(signature.ink);
  const [sending, setSending] = useState(false);

  useEffect(() => setInk(signature.ink), [signature.ink]);
  useEffect(
    () => () => {
      if (photo) URL.revokeObjectURL(photo);
    },
    [photo]
  );

  if (signature.status === "submitted")
    return (
      <Done
        title={t("sign.sent", { name: lead })}
        body={t("sign.sentBody", { name: lead })}
      />
    );
  if (signature.status === "completed")
    return (
      <Done
        title={t("sign.completed")}
        body={t("sign.completedBody", { name: lead })}
      />
    );

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    setPhoto(
      picked.type.startsWith("image/") ? URL.createObjectURL(picked) : null
    );
    setInk(null);
    setUploading(true);
    ClientPortalAPI.uploadInk(signature.id, picked)
      .then(setInk)
      .catch(() => toast.error(t("sign.uploadFailed")))
      .finally(() => setUploading(false));
  };

  const send = () => {
    setSending(true);
    ClientPortalAPI.submitInk(signature.id)
      .then(() => refreshClientPortal())
      .catch(() => toast.error(t("sign.failed")))
      .finally(() => setSending(false));
  };

  const hasPhoto = Boolean(ink) || uploading;
  const doubtful = Boolean(ink?.check.checked && !ink.check.ok);
  const disabled = preview || uploading || sending;
  const picker = (camera: boolean) => (
    <FilePick
      camera={camera}
      disabled={disabled}
      onChange={onFile}
      testId={camera ? "ink-camera" : "ink-file"}
      className={cn(
        "h-9 gap-2 rounded-md px-3",
        camera
          ? "bg-primary text-primary-foreground"
          : "border border-border bg-background"
      )}
    >
      {camera ? <Camera className="size-4" /> : <FileUp className="size-4" />}
      {camera ? t("sign.takePhoto") : t("sign.chooseFile")}
    </FilePick>
  );

  return (
    <>
      {signature.returned_note ? (
        <div className="flex flex-col gap-1 rounded-lg border border-warn-line bg-warn-soft px-3.5 py-3 text-sm text-warn">
          <span className="font-medium">
            {t("sign.returned", { name: lead })}
          </span>
          <span className="italic">“{signature.returned_note}”</span>
        </div>
      ) : null}
      <ol className="flex flex-col gap-5">
        <Step n={1} title={t("sign.print")}>
          <Button
            variant="outline"
            className="self-start"
            disabled={!file}
            onClick={() => file && download(file)}
          >
            <Download />
            {t("sign.download")}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t("sign.noPrinter")}
          </span>
        </Step>
        <Step n={2} title={t("sign.inkSign")}>
          <ul className="flex flex-col gap-1 text-sm">
            {boxLine(signature, t).map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1.5 size-2.5 shrink-0 rounded-sm bg-warn ring-1 ring-warn" />
                {line}
              </li>
            ))}
          </ul>
        </Step>
        <Step
          n={3}
          title={t("sign.photo")}
          done={Boolean(ink?.check.checked && ink.check.ok)}
        >
          <span className="text-sm text-muted-foreground">
            {t("sign.photoHint")}
          </span>
          {hasPhoto ? (
            <div className="flex gap-3">
              {photo ? (
                <img
                  src={photo}
                  alt=""
                  className="h-24 w-20 shrink-0 rounded-md border border-border object-cover"
                />
              ) : null}
              <UploadCheckCard
                name={ink?.filename ?? ""}
                check={uploading ? undefined : ink?.check}
                signature
                className="flex-1"
                actions={
                  uploading ? null : (
                    <FilePick
                      camera
                      disabled={disabled}
                      onChange={onFile}
                      className="gap-1.5 text-primary"
                    >
                      <RotateCcw className="size-3.5" />
                      {t("sign.retake")}
                    </FilePick>
                  )
                }
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {picker(true)}
              {picker(false)}
            </div>
          )}
        </Step>
      </ol>
      <Button
        size="lg"
        className="w-full"
        variant={doubtful ? "outline" : "default"}
        disabled={disabled || !ink}
        title={preview ? t("sign.previewOnly") : undefined}
        onClick={send}
        data-testid="ink-submit"
      >
        <Send />
        {sending
          ? t("sign.sending")
          : doubtful
          ? t("sign.sendAnyway")
          : t("sign.send", { name: lead })}
      </Button>
      <p className="text-xs text-muted-foreground">
        {t("sign.keepOriginal", { name: lead })}
      </p>
    </>
  );
};

const Back: FC = () => {
  const { t } = usePortalT();
  return (
    <Button asChild variant="ghost" size="sm" className="-ms-2 self-start">
      <Link to={CLIENT_PORTAL_TASKS_PATH}>
        <ArrowLeft className="rtl:rotate-180" />
        {t("sign.back")}
      </Link>
    </Button>
  );
};

const Unavailable: FC<{ title: string; body?: string }> = ({ title, body }) => (
  <>
    <Back />
    <Empty className="border border-border bg-background">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <CheckCircle2 />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {body ? <EmptyDescription>{body}</EmptyDescription> : null}
      </EmptyHeader>
    </Empty>
  </>
);

/** `/portal/sign/:requestId`: the document beside the steps to sign it. */
export const PortalSignPage: FC = () => {
  const me = useClientPortal();
  const { t } = usePortalT();
  const { requestId } = useParams<{ requestId: string }>();
  const query = useClientPortalSignature(requestId);
  const signature = query.data;
  const ready = signature && signature.status !== "upcoming";
  const file = useClientPortalSignatureFile(
    ready ? requestId : undefined,
    signature?.status
  );

  if (query.isLoading) return <LoadingState size="lg" />;
  if (query.error) {
    const code = portalErrorCode(query.error);
    if (code === "cancelled" || code === "gone" || code === null)
      return <Unavailable title={t("sign.gone")} />;
    return <PortalLoadError onRetry={() => void query.refetch()} />;
  }
  if (!signature) return <Unavailable title={t("sign.gone")} />;
  if (signature.status === "upcoming")
    return (
      <Unavailable
        title={t("sign.upcoming")}
        body={t("sign.upcomingBody", { name: senderFirst(signature, t) })}
      />
    );

  const ink = signature.kind === "ink";
  const notes = ink ? boxLine(signature, t) : [];
  const header = (
    <div className="flex flex-col gap-1">
      <h1 className="text-xl font-semibold tracking-tight">
        {signature.title}
      </h1>
      <p className="text-sm text-muted-foreground">
        {ink
          ? t("sign.inkWhy")
          : signature.from
          ? t("sign.from", { name: signature.from })
          : null}
      </p>
      {signature.message ? (
        <p className="mt-1 rounded-lg bg-fill-1 px-3 py-2 text-sm italic">
          “{signature.message}”
        </p>
      ) : null}
    </div>
  );

  return (
    <>
      <Back />
      <div className="lg:hidden">{header}</div>
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
        <PortalCard className="bg-fill-1 p-3 md:p-4">
          {file.data ? (
            <PortalPdf
              data={file.data.data}
              label={t("sign.viewer")}
              pageNote={ink ? (i) => notes[i] ?? null : undefined}
            />
          ) : file.isError ? (
            <PortalLoadError onRetry={() => void file.refetch()} />
          ) : (
            <Skeleton className="aspect-[8.5/11] w-full rounded-md" />
          )}
        </PortalCard>
        <PortalCard className="gap-5 lg:sticky lg:top-0">
          <div className="hidden lg:block">{header}</div>
          {ink ? (
            <InkSteps
              signature={signature}
              file={file.data}
              preview={me.preview}
            />
          ) : (
            <EsignSteps
              signature={signature}
              file={file.data}
              preview={me.preview}
              defaultName={[
                me.client.first_name,
                me.client.middle_name,
                me.client.last_name
              ]
                .filter(Boolean)
                .join(" ")}
            />
          )}
        </PortalCard>
      </div>
    </>
  );
};
